import type { ConfigElement, ConfigModule, HeadlineGroup, ModuleCategory } from '~/types/config';
import { mapElementTypeToHtmlType } from '~/Common/HtmlTypes';
import { cleanText } from '~/Common/StringOperations';
import type { LocalizedElementDto } from '~/types/graphql';
import { translateMatchingPattern } from '~/Common/useI18nTranslations';
import { parseElementOptions } from '~/Common/ConfigHelpers';

/**
 * Creates a ConfigElement object with the specified properties.
 * @param element - The configuration element to process.
 * @param t
 * @return A ConfigElement object with the processed properties.
 */
export function createElement(
  element: LocalizedElementDto,
  t: (k: string, p?: Record<string, any>) => string = (s) => s
): ConfigElement {
  const htmlType = mapElementTypeToHtmlType(element.elementType);
  return {
    id: element.id,
    elementName: element.elementName,
    translation: cleanText(element.translation),
    elementValue: element.elementValue,
    elementPath: element.elementPath,
    htmlType,
    headline: translateMatchingPattern(
      element.headline || t('config.form.settings'),
      'config.form',
      /^(realm|connection|mapping)\s+(.+)$/i,
      /^(settings|realm|connection|mapping)$/i,
      t
    ),
    options: element.options && htmlType === 'select'
      ? parseElementOptions(element.options as string)
      : undefined,
    isRequired: element.isRequired || false,
    order: element.order,
  } as ConfigElement;
}


/**
 * Creates a ModuleCategory object with the specified properties.
 * @param categoryName - The name of the category.
 * @param categoryXpath - The XPath of the category.
 * @param headlineGroups - An optional array of HeadlineGroup objects to include in the category.
 * @param isDeletable - A boolean indicating if the category can be deleted.
 * @param t
 * @return A ModuleCategory object with the specified properties.
 */
export function createCategory(
  categoryName: string,
  categoryXpath: string,
  isDeletable: boolean = true,
  headlineGroups: HeadlineGroup[] = [],
  t: (k: string, p?: Record<string, any>) => string = (s) => s
): ModuleCategory {
  return {
    categoryName: categoryName,
    translation:  translateMatchingPattern(
      categoryName,
      'config.form',
      /^(realm|connection|mapping)\s+(.+)$/i,
      /^(settings|realm|connection|mapping)$/i,
      t
    ),
    headlineGroups: headlineGroups,
    xpath: categoryXpath,
    hidden: false,
    isDeletable: isDeletable && !categoryName.toLowerCase().includes('settings'),
  } as ModuleCategory;
}

/**
 * Creates a configuration module from a block of localized configuration data.
 * @param block - The configuration block containing the module's data.
 * @param moduleXpath - The XPath of the module.
 * @param categories - An optional record of categories to include in the module.
 * @return A ConfigModule object with the processed properties.
 */
export function createModule(
  block: { id: number; subnodeName: string; translation: string },
  moduleXpath: string,
  categories: Record<string, ModuleCategory> = {}
): ConfigModule {
  return {
    moduleId: block.id,
    moduleName: block.subnodeName,
    translation: cleanText(block.translation),
    xpath: moduleXpath,
    categories
  };
}


/**
 * Creates filtered modules containing only specific elements based on differences
 * @param differencesByModule - Map of module differences grouped by module ID
 * @param organizedData - Original organized configuration data (readonly)
 * @returns Array of ConfigModule objects containing only specified elements
 */
export function createFilteredModules(
  differencesByModule: ReadonlyMap<number, readonly { id: number; elementValue: string }[]>,
  organizedData: Readonly<Record<number, ConfigModule>>
): ConfigModule[] {
  const filteredModules: ConfigModule[] = [];

  differencesByModule.forEach((moduleDifferences, moduleId) => {
    const originalModule = organizedData[moduleId];
    if (!originalModule) {
      console.warn(`Original module with id ${moduleId} not found`);
      return;
    }

    const filteredModule = createModule(
      {
        id: originalModule.moduleId,
        subnodeName: originalModule.moduleName,
        translation: originalModule.translation
      } as any,
      originalModule.xpath
    );

    // Process each difference for this module
    moduleDifferences.forEach(diff => {
      addElementToModule(filteredModule, originalModule, diff);
    });

    if (Object.keys(filteredModule.categories).length > 0) {
      filteredModules.push(filteredModule);
    }
  });

  return filteredModules;
}

/**
 * Adds an element to a filtered module structure based on element ID and new value
 * @param targetModule - The module being built with elements (will be mutated)
 * @param sourceModule - The original module structure to search in (readonly)
 * @param elementData - The element data containing id and new value (readonly)
 */
function addElementToModule(
  targetModule: ConfigModule,
  sourceModule: Readonly<ConfigModule>,
  elementData: Readonly<{ id: number; elementValue: string }>
): void {
  // Search through categories and headline groups in the source
  for (const [categoryName, category] of Object.entries(sourceModule.categories) as [string, ModuleCategory][]) {
    for (const headlineGroup of category.headlineGroups) {
      const targetElement = headlineGroup.elements.find(element => element.id === elementData.id);

      if (targetElement) {
        // Ensure category exists in target module with a copy
        if (!targetModule.categories[categoryName]) {
          targetModule.categories[categoryName] = {
            ...category,
            headlineGroups: []
          };
        }

        // Ensure headline group exists with a copy
        let targetHeadlineGroup = targetModule.categories[categoryName].headlineGroups
          .find(group => group.headline === headlineGroup.headline);

        if (!targetHeadlineGroup) {
          targetHeadlineGroup = {
            headline: headlineGroup.headline,
            elements: []
          };
          targetModule.categories[categoryName].headlineGroups.push(targetHeadlineGroup);
        }

        // Add the element with updated value (create new object, don't mutate original)
        targetHeadlineGroup.elements.push({
          ...targetElement,
          elementValue: elementData.elementValue
        });
        return;
      }
    }
  }
}